import { client, authHeaders } from '../../_shared/services/openapiClient';
import type { paths } from '../../../types/schema';

type LegacyApiResult<T> = Promise<{ data?: T; error?: unknown }>;
const legacyClient = client as unknown as {
  GET: <T>(path: string, options?: unknown) => LegacyApiResult<T>;
  POST: <T>(path: string, options?: unknown) => LegacyApiResult<T>;
};

// Types derived from OpenAPI schema
export type SearchBettieQuery = paths['/api/v1/bettie/search']['get']['parameters']['query'];
export type SearchBettieResponse =
  paths['/api/v1/bettie/search']['get']['responses'][200]['content']['application/json'];

export type CreateChatRoomBody = NonNullable<
  paths['/api/v1/bettie/create_chat_room']['post']['requestBody']
>['content']['application/json'];
export type CreateChatRoomResponse =
  paths['/api/v1/bettie/create_chat_room']['post']['responses'][200]['content']['application/json'];

export type CreateMessageBody = NonNullable<
  paths['/api/v1/bettie/create_message']['post']['requestBody']
>['content']['application/json'];
export type CreateMessageResponse =
  paths['/api/v1/bettie/create_message']['post']['responses'][201]['content']['application/json'];

export type GetBettieChatRoomsCountResponse =
  paths['/api/v1/bettie/chat_rooms_count']['get']['responses'][200]['content']['application/json'];

export type ResendBettieChatMessageResponse =
  paths['/api/v1/bettie/chat_messages/{chat_room_message_id}/resend']['post']['responses'][201]['content']['application/json'];

export type GetBettieDailyCheckInStatusResponse = NonNullable<
  paths['/api/v1/bettie/daily_check_in_reward_status']['get']['responses'][200]['content']['application/json']
>['bettieTransactions'];

export type ClaimBettieDailyCheckInRewardResponse =
  paths['/api/v1/bettie/daily_check_in_reward']['post']['responses'][200]['content']['application/json'];

export type UpdateBettieMoodSelectionBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/update_context']['post']['requestBody']
>['content']['application/json'];
export type UpdateBettieMoodSelectionResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/update_context']['post']['responses'][200]['content']['application/json'];

export type GetBettieDailyRandomRewardsResponse =
  paths['/api/v1/bettie/daily_random_rewards']['post']['responses'][200]['content']['application/json'];

export type GetBettieInfoResponse =
  paths['/api/v1/bettie/{bettie_id}']['get']['responses'][200]['content']['application/json']['bettie'];

export type GetBettieChatRoomPreferencesResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/user_preference']['get']['responses'][200]['content']['application/json']['context'];

export type GetBettieProactiveMessagesResponse = {
  proactiveMessages: Array<{
    proactiveMessageHistoryId: number;
    content: string;
    messageType?: string | null;
    groupType?: string | null;
    sentAt?: string | null;
    popupAction?: unknown;
  }>;
};

export type ActOnBettieProactiveMessageBody = {
  action: {
    type: 'later' | 'dislike';
  };
};
export type ActOnBettieProactiveMessageResponse = Record<string, unknown>;

export type UpdateBettieChatRoomPreferencesBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/update_context']['post']['requestBody']
>['content']['application/json'];
export type UpdateBettieChatRoomPreferencesResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/update_context']['post']['responses'][200]['content']['application/json']['chatRoom'];

export async function searchBettie(params: SearchBettieQuery): Promise<SearchBettieResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/search', {
    params: { query: params },
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response from bettie search');
  return data;
}

export async function createBettieChatRoom(
  body: CreateChatRoomBody
): Promise<CreateChatRoomResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/create_chat_room', {
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when creating chat room');
  return data;
}

export async function createBettieMessage(body: CreateMessageBody): Promise<CreateMessageResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/create_message', {
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when creating message');
  return data;
}

export async function getBettieChatRoomsCount(): Promise<GetBettieChatRoomsCountResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/chat_rooms_count', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie chat rooms count');
  return data;
}

export async function resendBettieChatMessage(
  chatRoomMessageId: string
): Promise<ResendBettieChatMessageResponse> {
  const { data, error } = await client.POST(
    '/api/v1/bettie/chat_messages/{chat_room_message_id}/resend',
    {
      params: { path: { chat_room_message_id: chatRoomMessageId } },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when resending bettie chat message');
  return data;
}

export async function deleteBettieChatMessage(chatRoomMessageId: string): Promise<void> {
  const { error } = await client.DELETE(
    '/api/v1/bettie/chat_messages/{chat_room_message_id}/delete',
    {
      params: { path: { chat_room_message_id: chatRoomMessageId } },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  return;
}

export async function getBettieDailyCheckInStatus(): Promise<GetBettieDailyCheckInStatusResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/daily_check_in_reward_status', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie daily check in reward status');
  return data.bettieTransactions;
}

export async function claimBettieDailyCheckInReward(): Promise<ClaimBettieDailyCheckInRewardResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/daily_check_in_reward', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when claiming bettie daily check in reward');
  return data;
}

export async function updateBettieMoodSelection(
  chatRoomId: string,
  body: UpdateBettieMoodSelectionBody
): Promise<UpdateBettieMoodSelectionResponse> {
  const { data, error } = await client.POST(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/update_context',
    {
      params: { path: { chat_room_id: chatRoomId } },
      body,
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when updating bettie mood selection');
  return data;
}

export async function getBettieDailyRandomRewards(): Promise<GetBettieDailyRandomRewardsResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/daily_random_rewards', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie daily random rewards');
  return data;
}

export async function getBettieInfo(bettieId: string): Promise<GetBettieInfoResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/{bettie_id}', {
    params: { path: { bettie_id: bettieId } },
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie info');
  return data.bettie;
}

export async function getBettieChatRoomPreferences(
  chatRoomId: string
): Promise<GetBettieChatRoomPreferencesResponse> {
  const { data, error } = await client.GET(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/user_preference',
    {
      params: { path: { chat_room_id: chatRoomId } },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie chat room preferences');
  return data.context;
}

export async function getBettieProactiveMessages(
  chatRoomId: string
): Promise<GetBettieProactiveMessagesResponse> {
  const { data, error } = await legacyClient.GET<GetBettieProactiveMessagesResponse>(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/proactive_messages',
    {
      params: { path: { chat_room_id: chatRoomId } },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie proactive messages');
  return data;
}

export async function actOnBettieProactiveMessage(
  chatRoomId: string,
  proactiveMessageHistoryId: number,
  body: ActOnBettieProactiveMessageBody
): Promise<ActOnBettieProactiveMessageResponse> {
  const { data, error } = await legacyClient.POST<ActOnBettieProactiveMessageResponse>(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/proactive_messages/{proactive_message_history_id}/actions',
    {
      params: {
        path: {
          chat_room_id: chatRoomId,
          proactive_message_history_id: proactiveMessageHistoryId
        }
      },
      body,
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when acting on bettie proactive message');
  return data;
}

export async function updateBettieChatRoomPreferences(
  chatRoomId: string,
  body: UpdateBettieChatRoomPreferencesBody
): Promise<UpdateBettieChatRoomPreferencesResponse> {
  const { data, error } = await client.POST(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/update_context',
    {
      params: { path: { chat_room_id: chatRoomId } },
      body,
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when updating bettie chat room preferences');
  return data.chatRoom;
}
